import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { ImpactStats } from "../sections/ImpactStats";
import { Partners } from "../sections/Partners";
import { CTA } from "../sections/CTA";
import { colors, fonts, media, radius } from "../theme";

const team = [
  { i: "JV", n: "Jerry Vance Anguzu", r: "CEO and Founder", bio: "Software engineer and data scientist. MIT Emerging Talent alumnus. Has built and shipped data and product for African fintechs." },
  { i: "DB", n: "Dembe Bruno", r: "Credit and Risk Lead", bio: "Credit manager with deep experience in banking sector portfolios, collections and risk. Previously at Platinum Credit." },
  { i: "AA", n: "Alan Abila", r: "COO", bio: "Fintech operations leader. Previously at MarketForce and Flip pay, and a contributor to Uganda's E Visa and Permit System." },
  { i: "IE", n: "Irene Eyogyiire", r: "Legal and Compliance", bio: "Finance, tax and infrastructure law. MSc in Law and Accounting from the London School of Economics." },
];

const supporting = [
  { n: "Bogere Siraje", r: "Chief Technology Officer" },
  { n: "Nakunda Brenda", r: "Data Science" },
  { n: "Senyange Eric", r: "Research and Development" },
];

const journey = [
  { y: "2023", t: "Founded in Kampala", b: "Everpesa is registered with a focus on credit access for African small businesses." },
  { y: "2024", t: "First SACCO and loan management systems shipped", b: "We built core banking and loan origination tools used by Ugandan cooperatives." },
  { y: "2025", t: "Peer to peer lending platform goes live", b: "invest.everpesa.com launches. The first loans are funded and repaid through the marketplace." },
  { y: "2026", t: "Scaling the marketplace", b: "USSD rollout, regional expansion across Uganda, and new SACCO and supply chain partnerships." },
];

export const About = () => (
  <>
    <PageHeader
      eyebrow="About Everpesa"
      title="Built in Kampala, for the entrepreneurs banks overlook."
      sub="Everpesa Technologies Ltd is a fintech with a simple mission: turn affordable digital credit into the engine of African small business growth. We are licensed, regulated, and accountable to the people we serve."
    />

    <Section>
      <Container>
        <Mission>
          <div>
            <Eyebrow>Mission</Eyebrow>
            <h2 style={{ marginTop: 14, fontFamily: fonts.display }}>
              Connecting investors to women-led businesses.
              <br /><Hl>Affordable credit. Real returns. Real impact.</Hl>
            </h2>
          </div>
          <p style={{ fontSize: 17 }}>
            Most small businesses in Uganda cannot borrow from a bank. The
            alternatives are usually informal lenders that compound quickly. We
            exist to give entrepreneurs a fairer third option. To do that we
            built a peer to peer marketplace, an alternative credit score, and
            a stack of supporting products that all push the same direction.
          </p>
        </Mission>
      </Container>
    </Section>

    <Section id="journey" style={{ background: colors.surfaceAlt }}>
      <Container>
        <Eyebrow>Our journey</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 40, fontFamily: fonts.display }}>From a Kampala office to a working marketplace.</h2>
        <Timeline>
          {journey.map((j) => (
            <TItem key={j.y}>
              <Year>{j.y}</Year>
              <h3>{j.t}</h3>
              <p>{j.b}</p>
            </TItem>
          ))}
        </Timeline>
      </Container>
    </Section>

    <Section id="team">
      <Container>
        <Eyebrow>The team</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 8, fontFamily: fonts.display }}>Builders, operators, and credit people.</h2>
        <p style={{ fontSize: 17, marginBottom: 40, maxWidth: 640 }}>
          We have shipped financial products for African banks, microfinance
          institutions and government, and now we ship for ourselves and the
          entrepreneurs we serve.
        </p>

        <TeamGrid>
          {team.map((m) => (
            <Member key={m.i}>
              <Avatar>{m.i}</Avatar>
              <h3>{m.n}</h3>
              <Role>{m.r}</Role>
              <p>{m.bio}</p>
            </Member>
          ))}
        </TeamGrid>

        <Sub>
          <h4>Supporting team</h4>
          <SubList>
            {supporting.map((s) => (
              <SubItem key={s.n}>
                <strong>{s.n}</strong>
                <span>{s.r}</span>
              </SubItem>
            ))}
          </SubList>
        </Sub>
      </Container>
    </Section>

    <ImpactStats />
    <Partners />
    <CTA />
  </>
);

const Mission = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: end;
  ${media.md} { grid-template-columns: 1fr; gap: 24px; }
`;

const Hl = styled.span`
  background: linear-gradient(90deg, ${colors.brand}, ${colors.gold});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Timeline = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  ${media.md} { grid-template-columns: 1fr; }
`;

const TItem = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  padding: 24px;
  position: relative;
  border-top: 4px solid ${colors.brand};
  h3 { font-family: ${fonts.display}; font-size: 17px; margin-top: 8px; }
  p { font-size: 14.5px; margin-top: 8px; line-height: 1.6; }
`;

const Year = styled.span`
  font-family: ${fonts.display};
  font-weight: 800;
  font-size: 28px;
  color: ${colors.brand};
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 56px;
  ${media.lg} { grid-template-columns: repeat(2, 1fr); }
  ${media.sm} { grid-template-columns: 1fr; }
`;

const Member = styled.div`
  background: ${colors.surfaceAlt};
  border-radius: ${radius.lg};
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  h3 { font-family: ${fonts.display}; font-size: 17px; margin-top: 14px; }
  p { font-size: 13.5px; margin-top: 10px; line-height: 1.6; color: ${colors.inkMuted}; }
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${colors.brand};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.display};
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.05em;
`;

const Role = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.brand};
  letter-spacing: 0.04em;
`;

const Sub = styled.div`
  border-top: 1px solid ${colors.line};
  padding-top: 28px;
  h4 {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${colors.inkSoft};
    margin-bottom: 16px;
    font-family: ${fonts.body};
  }
`;

const SubList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  ${media.sm} { grid-template-columns: 1fr; }
`;

const SubItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  strong { font-family: ${fonts.display}; color: ${colors.ink}; }
  span { font-size: 13px; color: ${colors.inkSoft}; }
`;
